import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { calendarTeamId } from '../../extensions/bundled/calendar/calendarStore';
import { useTeamsStore } from '../../store/teamsStore';
import { expandEvents } from '../../utils/calendarRecurrence';
import {
  useCalendarInvitesStore, ensureInvitationsLoaded, snapshotToEventFields, organizerOfRow,
} from '../../services/calendarInvitesService';
import { useAuthStore } from '../../store/authStore';
import {
  useCalendarUiStore,
  bumpCalendarEvents,
} from '../../extensions/bundled/calendar/calendarUiStore';
import { usePreferencesStore } from '../../store/preferencesStore';
import { addDays, addMonths, startOfDay, toDateKey, monthGridDays, weekDays } from '../../utils/calendarDates';
import { isTauri } from '../../utils/tauri';
import '../Calendar.css';
import { isTypingTarget, matchesCalendarFilter, useCalendarEvents } from './calendarViewHelpers';
import { renderCalendarView } from './renderCalendarView';
import { useCalendarEventActions } from './useCalendarEventActions';
export { isReadOnlyEvent, matchesCalendarFilter, roleForTeamId, useCalendarEvents } from './calendarViewHelpers';

/**
 * Calendar main view (host-rendered panel content for `calendar.panel`):
 * toolbar + the active Month/Week/Day/Agenda view, the event popover/editor,
 * the reminder scheduler, and the keyboard map (T today · M/W/D/A views ·
 * arrows move the focused day · N new event · Esc closes — PLAN §3.5).
 */
export default function CalendarView() {
  const { t } = useTranslation();
  const view = useCalendarUiStore((s) => s.view);
  const focusedDateMs = useCalendarUiStore((s) => s.focusedDateMs);
  const hiddenCalendars = useCalendarUiStore((s) => s.hiddenCalendars);
  const calendarFilter = useCalendarUiStore((s) => s.calendarFilter);
  const setView = useCalendarUiStore((s) => s.setView);
  const setFocusedDate = useCalendarUiStore((s) => s.setFocusedDate);
  const setCalendarFilter = useCalendarUiStore((s) => s.setCalendarFilter);
  const timeFormat = usePreferencesStore((s) => s.timeFormat);

  const events = useCalendarEvents();
  const focusedDate = new Date(focusedDateMs);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const teams = useTeamsStore((s) => s.teams);
  const currentUser = useAuthStore((s) => s.user);
  const pendingInvitations = useCalendarInvitesStore((s) => s.pending);

  // Pending invitations render IN-GRID as ghost chips (PLAN §3.5): pseudo
  // events built from the invitation snapshot, marked with `pending_invite`
  // so chips/popover switch to the dashed Pending treatment + Accept/Decline.
  const ghostEvents = pendingInvitations
    .filter((row) => row?.snapshot)
    .map((row) => ({
      uid: `invite:${row.id ?? row.token}`,
      ...snapshotToEventFields(row.snapshot),
      description: '',
      calendar: 'personal',
      color: null,
      reminders: [],
      status: 'confirmed',
      pending_invite: {
        token: row.token,
        appointment_uid: row.appointment_uid,
        // Wire key is `organizer` on fetched rows (tester N2/F3) —
        // organizerOfRow covers both shapes so the popover shows the name.
        invited_by: organizerOfRow(row).name ? { name: organizerOfRow(row).name } : null,
        row,
      },
    }));

  // Phase 4: ONE memoized expansion between the raw rows and the filter —
  // window derived from view+focusedDate, padded ±1 day; `events` is a new
  // array on every eventsVersion bump, which re-keys the memo for free.
  const [winStart, winEnd] = (() => {
    if (view === 'month') {
      const days = monthGridDays(focusedDate);
      return [addDays(days[0], -1), addDays(days[41], 2)];
    }
    if (view === 'week') {
      const days = weekDays(focusedDate);
      return [addDays(days[0], -1), addDays(days[6], 2)];
    }
    if (view === 'agenda') return [addDays(startOfDay(focusedDate), -1), addDays(startOfDay(focusedDate), 31)];
    return [addDays(startOfDay(focusedDate), -1), addDays(startOfDay(focusedDate), 2)];
  })();
  const winStartKey = toDateKey(winStart);
  const winEndKey = toDateKey(winEnd);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const expandedEvents = React.useMemo(
    () => expandEvents(events, winStart, winEnd),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [events, winStartKey, winEndKey]
  );

  // Two layers: the persisted per-calendar toggles (Phase 3 UI) and the
  // toolbar's Personal/Team/All filter (fix round 3).
  const visibleEvents = expandedEvents
    .filter(
      (e) => !hiddenCalendars[e.calendar || 'personal'] && matchesCalendarFilter(e, calendarFilter)
    )
    // Coloring is BY CALENDAR (owner, 30.08): personal = the app accent
    // (chips' DEFAULT fallback), team = the team's color. A render-time
    // decoration, never written back; the record's own `color` is DEPRECATED
    // and deliberately IGNORED here so historical values can't fragment the
    // scheme (tolerated on read in the store, §3.2 amendment).
    .map((e) => {
      const teamId = calendarTeamId(e.calendar);
      if (teamId == null) return e.color ? { ...e, color: null } : e;
      const team = (teams || []).find((t) => String(t?.id) === String(teamId));
      return { ...e, color: team?.color || null };
    })
    .concat(calendarFilter === 'team' ? [] : ghostEvents);

  const [popover, setPopover] = useState(null); // { event, anchorRect }
  const [editorDraft, setEditorDraft] = useState(null); // partial event | null
  const [deleteTarget, setDeleteTarget] = useState(null); // event | null
  const [importReport, setImportReport] = useState(null); // { imported, skipped }
  const [icsBusy, setIcsBusy] = useState(false);
  const [eventMenu, setEventMenu] = useState(null); // { event, x, y }
  // Phase 4: This event / All events scope dialog for recurring occurrences.
  const [scopeDialog, setScopeDialog] = useState(null); // { mode, occurrence, fields?, extra? }

  // First calendar open of the session loads the viewer's invitations
  // (pending ghosts + accepted gap-fill); realtime + rejoin keep them fresh.
  useEffect(() => {
    if (isAuthenticated) ensureInvitationsLoaded();
  }, [isAuthenticated]);

  // The reminder scheduler is deliberately NOT started (or stopped) here:
  // it is a boot service owned by App.js (fix round 1, B1) — a mount-scoped
  // start/stop would kill the global interval whenever the user switches tabs.

  // Titlebar "New event" (ExtensionTitleBarActions dispatches the CustomEvent).
  useEffect(() => {
    const onNew = () => setEditorDraft({});
    window.addEventListener('calendar-new-event', onNew);
    return () => window.removeEventListener('calendar-new-event', onNew);
  }, []);

  // A personal-scope pull from another device changed records → re-read
  // (useSyncEngine bumps documents/boards this way; the calendar subscribes
  // for itself so no shared file needs a calendar branch).
  useEffect(() => {
    if (!isTauri()) return undefined;
    let unsub = null;
    let cancelled = false;
    (async () => {
      const [{ subscribeSyncEvent, SYNC_EVENTS }, { personalScopeId }] = await Promise.all([
        import('../../services/syncStore'),
        import('../../services/personalScope'),
      ]);
      if (cancelled) return;
      unsub = subscribeSyncEvent(SYNC_EVENTS.REMOTE_APPLIED, (payload) => {
        const wsId = String(payload?.workspaceId || '');
        // Personal pulls AND team-channel pulls (Phase 3: team events ride
        // the same team:<id> scopes boards/documents already refresh on).
        if (wsId === personalScopeId() || wsId.startsWith('team:')) bumpCalendarEvents();
      });
    })();
    return () => {
      cancelled = true;
      if (unsub) unsub();
    };
  }, []);

  const navigate = useCallback(
    (direction) => {
      const d = new Date(useCalendarUiStore.getState().focusedDateMs);
      const v = useCalendarUiStore.getState().view;
      if (v === 'month') setFocusedDate(addMonths(d, direction));
      else if (v === 'week') setFocusedDate(addDays(d, 7 * direction));
      else setFocusedDate(addDays(d, direction)); // day + agenda page by day
    },
    [setFocusedDate]
  );

  // Keyboard map. Window-level is safe: this component only mounts while the
  // calendar tab is active, and typing targets/modals are excluded.
  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      if (editorDraft || deleteTarget) return; // modal owns the keyboard
      const key = e.key.toLowerCase();
      if (key === 't') setFocusedDate(new Date());
      else if (key === 'm') setView('month');
      else if (key === 'w') setView('week');
      else if (key === 'd') setView('day');
      else if (key === 'a') setView('agenda');
      else if (key === 'n') setEditorDraft({});
      else if (key === 'escape') setPopover(null);
      else if (key === 'arrowleft') setFocusedDate(addDays(new Date(useCalendarUiStore.getState().focusedDateMs), -1));
      else if (key === 'arrowright') setFocusedDate(addDays(new Date(useCalendarUiStore.getState().focusedDateMs), 1));
      else if (key === 'arrowup') setFocusedDate(addDays(new Date(useCalendarUiStore.getState().focusedDateMs), -7));
      else if (key === 'arrowdown') setFocusedDate(addDays(new Date(useCalendarUiStore.getState().focusedDateMs), 7));
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editorDraft, deleteTarget, setFocusedDate, setView]);

  const handleSelectEvent = (event, anchorRect) => setPopover({ event, anchorRect });

  // Right-click on an event card, any view (fix round 4) — near-free with the
  // shared ContextMenu: Open/Edit/Delete route to the EXISTING single paths,
  // Duplicate is a fresh-uid copy on the same day (Outlook/Google standard).
  const handleEventContextMenu = (event, { x, y }) => setEventMenu({ event, x, y });

  const {
    handleUpdateEventTimesGuarded, handleDuplicateEvent, writeOverride, applyEditToMaster,
    deleteThisOccurrence, handleSaveDraft, requestDelete, confirmDelete, handleInviteResponse,
    handleCreateDraftFromGrid, handleImportIcs, handleExportIcs, handleQuickCreate,
  } = useCalendarEventActions({
    t, teams, events, isAuthenticated, currentUser, setPopover, editorDraft, setEditorDraft,
    deleteTarget, setDeleteTarget, setImportReport, icsBusy, setIcsBusy, setScopeDialog,
  });

  return renderCalendarView({
    t, teams, view, popover, focusedDate, calendarFilter, setView, setFocusedDate,
    setCalendarFilter, timeFormat, events, visibleEvents, setPopover, editorDraft, setEditorDraft,
    deleteTarget, setDeleteTarget, importReport, setImportReport, eventMenu, setEventMenu,
    scopeDialog, setScopeDialog, navigate, handleSelectEvent, handleEventContextMenu,
    handleUpdateEventTimesGuarded, writeOverride, handleDuplicateEvent, applyEditToMaster,
    deleteThisOccurrence, handleSaveDraft, requestDelete, confirmDelete, handleInviteResponse,
    handleCreateDraftFromGrid, handleImportIcs, handleExportIcs, handleQuickCreate,
  });
}
